import React, { useMemo } from 'react';
import moment from 'moment';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { Box, Divider, Grid, Slide, Stack, Typography } from '@material-ui/core';
import { FiClipboard, FiPrinter, FiTruck, FiUser } from 'react-icons/fi';

import CartPedidos from '../CartPedidos';
import {
  getStatusLabel,
  getShippingMethodLabel,
  getPaymentTypeLabel,
  getBandeiraGateway,
  PRICE_DISCOUNT_FACTOR,
} from '../../constants/pedidos';

const cardSx = {
  p: { xs: 2.5, md: 3 },
  borderRadius: 2.5,
  border: '1px solid',
  borderColor: 'grey.200',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  bgcolor: '#fff',
};

const sectionHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  pb: 1.5,
  borderBottom: '1px solid',
  borderColor: 'grey.200',
};

const labelSx = { fontSize: 10, color: '#999' };
const valueXlSx = { fontSize: 14, fontWeight: 700, color: '#333' };
const valueMdSx = { fontSize: 10, fontWeight: 600, color: '#333' };

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SectionHeader = ({ icon: Icon, title }) => (
  <Box sx={sectionHeaderSx}>
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        bgcolor: '#e8eaf0',
        color: '#1a1a2e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={18} />
    </Box>
    <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{title}</Typography>
  </Box>
);

const validaParcelasPedido = (responseZoop, gateway) => {
  if (gateway === 'pagarme') return responseZoop?.installment_plan ?? 1;
  return responseZoop?.installment_plan?.number_installments ?? 1;
};

const validaCodigoAutorizacao = (responseZoop, numeroPedido) => {
  if (responseZoop?.payment_type === 'pix') return `P-${numeroPedido}`;
  return responseZoop?.payment_authorization?.authorization_code ?? 'Não autorizado';
};

const formataStatusZoop = (value) => (value === 'succeeded' ? 'Aprovado' : value ?? '—');

const formatBRL = (value) => `R$ ${Number(value).toFixed(2)}`;

export const PedidoDetalhesDialog = ({ open, pedido, dadosFilial, onClose, onEmitirEtiqueta }) => {
  const shipping = pedido?.shipping_address;
  const items = pedido?.items ?? [];

  const subtotal = useMemo(() => {
    if (items.length === 0) return 0;
    return items.reduce((acc, { price, quantity }) => acc + parseFloat(price) * PRICE_DISCOUNT_FACTOR * quantity, 0);
  }, [items]);

  const total = useMemo(() => subtotal + Number(pedido?.shipping_price ?? 0), [subtotal, pedido?.shipping_price]);

  const moovpayResponse =
    pedido?.moovpay?.payment?.RetornoStatusCodigo === '00' &&
    pedido?.moovpay?.payment?.RetornoStatusCompra?.length > 0
      ? pedido.moovpay.payment.RetornoStatusCompra[0]
      : null;

  const responseZoop = pedido?.responseZoop;

  if (!pedido) return null;

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e5e5e5' }}>
        <Toolbar sx={{ maxWidth: '98%', mx: 'auto' }}>
          <IconButton edge="start" onClick={onClose} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 1, flex: 1, fontWeight: 700, fontSize: '1.15rem', color: '#1a1a2e' }}>
            Pedido #{pedido.number}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onEmitirEtiqueta}
            startIcon={<FiPrinter />}
            sx={{
              bgcolor: '#1a1a2e',
              '&:hover': { bgcolor: '#16213e' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            Gerar etiqueta
          </Button>
        </Toolbar>
      </Box>

      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Box sx={{ maxWidth: '98%', mx: 'auto', py: 3 }}>
          <Grid container spacing={3}>
            <Grid item md={9} xs={12}>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <Stack spacing={2}>
                    <Box sx={cardSx}>
                      <SectionHeader icon={FiUser} title="Informações Pessoais" />
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <Typography sx={labelSx}>nome do cliente</Typography>
                          <Typography sx={valueXlSx}>
                            {`${pedido.first_name ?? ''} ${pedido.last_name ?? ''}`.trim() || '—'}
                          </Typography>
                        </Grid>
                        <Grid item md={6} xs={6}>
                          <Typography sx={labelSx}>telefone</Typography>
                          <Typography sx={valueXlSx}>{shipping?.phone ?? '—'}</Typography>
                        </Grid>
                        <Grid item md={6} xs={6}>
                          <Typography sx={labelSx}>cpf cliente</Typography>
                          <Typography sx={valueXlSx}>{pedido.cpf || '—'}</Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box sx={cardSx}>
                      <SectionHeader icon={FiTruck} title={`Informações de ${pedido.tipoEntrega ?? 'Entrega'}`} />
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <Typography sx={labelSx}>enviar para</Typography>
                          {pedido.tipoEntrega === 'Entrega' && shipping ? (
                            <Stack spacing={0.5}>
                              <Typography sx={valueXlSx}>
                                {`${shipping.address1 ?? ''}, ${shipping.number ?? ''}, ${shipping.address2 ?? ''}`}
                                {shipping.details ? ` (${shipping.details})` : ''}
                              </Typography>
                              <Typography sx={valueXlSx}>
                                {`${shipping.postal_code ?? ''} - ${shipping.city ?? ''} / ${shipping.state ?? ''}`}
                              </Typography>
                            </Stack>
                          ) : dadosFilial ? (
                            <Typography sx={valueXlSx}>
                              <strong>Loja {dadosFilial.codigo_filial}:</strong> {dadosFilial.logradouro}, {dadosFilial.numero}, {dadosFilial.bairro}
                              {dadosFilial.complemento ? `, ${dadosFilial.complemento}` : ''}, {dadosFilial.localidade} - {dadosFilial.uf} - {dadosFilial.cep}
                            </Typography>
                          ) : (
                            <Typography sx={valueXlSx}>—</Typography>
                          )}
                        </Grid>
                        <Grid item md={6} xs={6}>
                          <Typography sx={labelSx}>quem vai entregar</Typography>
                          <Typography sx={valueXlSx}>{getShippingMethodLabel(pedido.shipping_method_id)}</Typography>
                        </Grid>
                        <Grid item md={6} xs={6}>
                          <Typography sx={labelSx}>valor da entrega</Typography>
                          <Typography sx={valueXlSx}>{formatBRL(pedido.shipping_price ?? 0)}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item md={6} xs={12}>
                  <Stack spacing={2}>
                    <Box sx={cardSx}>
                      <SectionHeader icon={FiClipboard} title="Informações do Pedido" />
                      <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                        <Grid item md={6} xs={6}>
                          <Typography sx={labelSx}>filial de origem</Typography>
                          <Typography sx={valueMdSx}>{items[0]?.filial ?? '—'}</Typography>
                        </Grid>
                        <Grid item md={6} xs={6}>
                          <Typography sx={labelSx}>data</Typography>
                          <Typography sx={valueMdSx}>{moment(pedido.date_created).format('DD/MM/YYYY')}</Typography>
                        </Grid>
                        <Grid item md={6} xs={6}>
                          <Typography sx={labelSx}>status do pedido</Typography>
                          <Typography sx={valueMdSx}>{getStatusLabel(pedido.status_id)}</Typography>
                        </Grid>
                        <Grid item md={6} xs={6}>
                          <Typography sx={labelSx}>o cliente escolheu</Typography>
                          <Typography sx={valueMdSx}>{pedido.tipoEntrega ?? '—'}</Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    {moovpayResponse && (
                      <Box sx={cardSx}>
                        <SectionHeader icon={FiClipboard} title="Informações da Transação" />
                        <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                          <Grid item md={4} xs={6}>
                            <Typography sx={labelSx}>Status</Typography>
                            <Typography sx={valueMdSx}>Aprovado</Typography>
                          </Grid>
                          <Grid item md={4} xs={6}>
                            <Typography sx={labelSx}>data</Typography>
                            <Typography sx={valueMdSx}>
                              {moment(moovpayResponse.DataAutorizacao).format('DD/MM/YYYY')}
                            </Typography>
                          </Grid>
                          <Grid item md={4} xs={6}>
                            <Typography sx={labelSx}>Código autorização</Typography>
                            <Typography sx={valueMdSx}>{moovpayResponse.NumeroAutorizacao}</Typography>
                          </Grid>
                          <Grid item md={4} xs={6}>
                            <Typography sx={labelSx}>NSU</Typography>
                            <Typography sx={valueMdSx}>{moovpayResponse.NSU}</Typography>
                          </Grid>
                          <Grid item md={4} xs={6}>
                            <Typography sx={labelSx}>Administradora</Typography>
                            <Typography sx={valueMdSx}>Moovpay</Typography>
                          </Grid>
                          <Grid item md={4} xs={6}>
                            <Typography sx={labelSx}>Parcelas</Typography>
                            <Typography sx={valueMdSx}>{moovpayResponse.QuantidadeParcelas}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography sx={labelSx}>Comprovante</Typography>
                            <Typography sx={valueMdSx}>{moovpayResponse.ComprovantePdv}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    {responseZoop && (
                      <Box sx={cardSx}>
                        <SectionHeader icon={FiClipboard} title="Informações da Transação" />
                        <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                          <Grid item md={4} xs={6}>
                            <Typography sx={labelSx}>Status</Typography>
                            <Typography sx={valueMdSx}>{formataStatusZoop(responseZoop.status)}</Typography>
                          </Grid>
                          <Grid item md={4} xs={6}>
                            <Typography sx={labelSx}>data</Typography>
                            <Typography sx={valueMdSx}>
                              {moment(responseZoop.created_at).format('DD/MM/YYYY')}
                            </Typography>
                          </Grid>
                          <Grid item md={4} xs={6}>
                            <Typography sx={labelSx}>Código autorização</Typography>
                            <Typography sx={valueMdSx}>{validaCodigoAutorizacao(responseZoop, pedido.number)}</Typography>
                          </Grid>
                          <Grid item md={3} xs={6}>
                            <Typography sx={labelSx}>Tipo Pagamento</Typography>
                            <Typography sx={valueMdSx}>{getPaymentTypeLabel(responseZoop.payment_type)}</Typography>
                          </Grid>
                          <Grid item md={3} xs={6}>
                            <Typography sx={labelSx}>Administradora</Typography>
                            <Typography sx={valueMdSx}>
                              {getBandeiraGateway(responseZoop.payment_type, responseZoop.point_of_sale?.gateway)}
                            </Typography>
                          </Grid>
                          <Grid item md={3} xs={6}>
                            <Typography sx={labelSx}>Parcelas</Typography>
                            <Typography sx={valueMdSx}>
                              {validaParcelasPedido(responseZoop, responseZoop.point_of_sale?.gateway)}
                            </Typography>
                          </Grid>
                          <Grid item md={3} xs={6}>
                            <Typography sx={labelSx}>Bandeira</Typography>
                            <Typography sx={valueMdSx}>{responseZoop?.payment_method?.card_brand ?? '—'}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Grid>

            <Grid item md={3} xs={12}>
              <Box sx={cardSx}>
                <CartPedidos cartItems={items} />
                <Box sx={{ mt: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>Subtotal</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>{formatBRL(subtotal)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                    <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>Frete</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                      {formatBRL(pedido.shipping_price ?? 0)}
                    </Typography>
                  </Stack>
                  <Divider sx={{ mt: 2, mb: 2 }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontWeight: 600, color: '#333' }}>Total Geral:</Typography>
                    <Typography sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: '1.05rem' }}>
                      {formatBRL(total)}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Dialog>
  );
};

export default PedidoDetalhesDialog;
